(() => {
  if (window.__UGBGiftBuilderLoaded) return;
  window.__UGBGiftBuilderLoaded = true;

  const BUILDER_KEYS = {
    bundleId: '_UGB Bundle ID',
    role: '_UGB Builder Role',
    parent: '_UGB Parent Product',
    category: '_UGB Builder Category',
    source: '_UGB Builder Source',
    giftContent: '_UGB Gift Content',
    fulfillmentContent: '_UGB Fulfillment Content',
    giftContents: '_UGB Gift Contents',
    fulfillmentContents: '_UGB Fulfillment Contents',
    summary: '_UGB Builder Summary'
  };

  function randomToken() {
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const values = new Uint32Array(2);
      window.crypto.getRandomValues(values);
      return Array.from(values, (value) => value.toString(36)).join('');
    }
    return Math.random().toString(36).slice(2, 12);
  }

  function buildBundleId() {
    return `UGB-BDL-${Date.now().toString(36)}-${randomToken()}`.toUpperCase();
  }

  function selectedOptions(builder) {
    return Array.from(builder.querySelectorAll('[data-ugb-addon-option]:checked'));
  }

  function packagingGroup(builder) {
    return builder.querySelector('[data-ugb-packaging-group]');
  }

  function standardPackagingOption(builder) {
    const group = packagingGroup(builder);
    if (!group) return null;

    const marked = group.querySelector('[data-standard-packaging="true"]');
    if (marked instanceof HTMLInputElement) return marked;

    return Array.from(group.querySelectorAll('[data-ugb-addon-option]')).find((option) => {
      const text = [
        option.dataset.giftContent,
        option.dataset.fulfillmentContent,
        option.dataset.variantTitle
      ].filter(Boolean).join(' ').toLowerCase();
      return text.includes('standard gift packaging') || text.includes('standard packaging');
    }) || null;
  }

  function selectedOptionsCents(builder) {
    return selectedOptions(builder).reduce((sum, option) => sum + (Number(option.dataset.priceCents) || 0), 0);
  }

  function selectedNonPackagingCents(builder) {
    return selectedOptions(builder).reduce((sum, option) => {
      if (option.closest('[data-ugb-packaging-group]')) return sum;
      return sum + (Number(option.dataset.priceCents) || 0);
    }, 0);
  }

  function mainPriceCents(builder) {
    return Number(builder.dataset.ugbMainPriceCents) || 0;
  }

  function standardPackagingEligibleCents(builder) {
    return mainPriceCents(builder) + selectedNonPackagingCents(builder);
  }

  function standardPackagingMaxCents(builder) {
    return Number(builder.dataset.ugbStandardPackagingMaxCents) || 13500;
  }

  function buildTotalCents(builder) {
    return mainPriceCents(builder) + selectedOptionsCents(builder);
  }

  function cleanText(value, fallback = '') {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    return text || fallback;
  }

  function fulfillmentLine(value) {
    const text = cleanText(value, 'Gift addition');
    return /^\d+\s*[×x]\s+/i.test(text) ? text : `1 × ${text}`;
  }

  function buildComposition(options) {
    const giftContents = [];
    const fulfillmentContents = [];
    const summary = [];

    for (const option of options) {
      const fallback = cleanText(option.dataset.variantTitle, 'Gift addition');
      const giftContent = cleanText(option.dataset.giftContent, fallback);
      const fulfillmentContent = cleanText(option.dataset.fulfillmentContent, giftContent);
      const category = cleanText(option.dataset.category, 'Gift addition');

      giftContents.push(giftContent);
      fulfillmentContents.push(fulfillmentLine(fulfillmentContent));
      summary.push(`${category}: ${giftContent}`);
    }

    fulfillmentContents.push('1 × Greeting card');

    return {
      giftContents: giftContents.join(' | '),
      fulfillmentContents: fulfillmentContents.join(' | '),
      summary: summary.join(' | ')
    };
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${currency || 'USD'}`;
    }
  }

  function standardPackagingLimitMessage(builder, itemsTotal = standardPackagingEligibleCents(builder)) {
    const limit = standardPackagingMaxCents(builder);
    return `Standard Gift Packaging is available only when the gift value before packaging is ${money(limit, builder.dataset.ugbCurrency)} or less. Your current gift value is ${money(itemsTotal, builder.dataset.ugbCurrency)}. Please choose Gift Box or Willow Gift Basket.`;
  }

  function syncStandardPackaging(builder) {
    const group = packagingGroup(builder);
    const standard = standardPackagingOption(builder);
    const note = group?.querySelector('[data-ugb-packaging-limit-note]');
    const itemsTotal = standardPackagingEligibleCents(builder);
    const limit = standardPackagingMaxCents(builder);
    const allowed = itemsTotal <= limit;
    const wasSelected = Boolean(standard?.checked);

    if (group) group.dataset.standardPackagingAllowed = allowed ? 'true' : 'false';

    if (standard instanceof HTMLInputElement) {
      const label = standard.closest('.ugb-gift-builder__option');

      if (!allowed && standard.checked) standard.checked = false;
      standard.disabled = !allowed;

      if (label) {
        label.classList.toggle('is-disabled', !allowed);
        if (allowed) label.removeAttribute('aria-disabled');
        else label.setAttribute('aria-disabled', 'true');
      }
    }

    if (note) {
      note.hidden = allowed;
      note.textContent = allowed ? '' : standardPackagingLimitMessage(builder, itemsTotal);
    }

    return { group, standard, allowed, wasSelected, itemsTotal, limit };
  }

  function updateGroup(group) {
    const checked = Array.from(group.querySelectorAll('[data-ugb-addon-option]:checked'));
    const required = group.dataset.required === 'true';
    group.classList.toggle('has-selection', checked.length > 0);
    group.classList.toggle('is-required-missing', required && checked.length === 0);

    const summary = group.querySelector('[data-ugb-group-summary]');
    if (!summary) return;
    if (checked.length) summary.textContent = `${checked.length} selected`;
    else summary.textContent = required ? 'Required' : 'Optional';
  }

  function updateBuilder(builder) {
    syncStandardPackaging(builder);
    builder.querySelectorAll('[data-ugb-builder-group]').forEach(updateGroup);

    const selected = selectedOptions(builder);
    const selection = builder.querySelector('[data-ugb-builder-selection]');
    const count = builder.querySelector('[data-ugb-builder-count]');
    const plural = builder.querySelector('[data-ugb-builder-count-plural]');
    const total = builder.querySelector('[data-ugb-builder-total]');
    if (!selection || !count || !plural || !total) return;

    if (!selected.length) {
      selection.hidden = true;
      return;
    }

    count.textContent = String(selected.length);
    plural.textContent = selected.length === 1 ? '' : 's';
    total.textContent = `Build total ${money(buildTotalCents(builder), builder.dataset.ugbCurrency)}`;
    selection.hidden = false;
  }

  function updateMessageGroup(group) {
    if (!(group instanceof HTMLDetailsElement)) return;
    const message = group.querySelector('[data-ugb-message]');
    const signature = group.querySelector('[data-ugb-signature]');
    const summary = group.querySelector('[data-ugb-message-summary]');
    const complete = Boolean(String(message?.value || '').trim() && String(signature?.value || '').trim());
    group.classList.toggle('is-complete', complete);
    if (summary) summary.textContent = complete ? 'Ready' : 'Required';
  }

  function initMessageGroups(root = document) {
    root.querySelectorAll?.('[data-ugb-builder-message-group]').forEach((group) => {
      if (!(group instanceof HTMLDetailsElement) || group.dataset.ugbMessageGroupReady === 'true') return;
      group.querySelectorAll('[data-ugb-message],[data-ugb-signature]').forEach((field) => {
        field.addEventListener('input', () => updateMessageGroup(group));
      });
      group.dataset.ugbMessageGroupReady = 'true';
      updateMessageGroup(group);
    });
  }

  function collectMainProperties(formData) {
    const properties = {};
    for (const [key, rawValue] of formData.entries()) {
      const match = /^properties\[(.*)\]$/.exec(key);
      if (!match) continue;
      const value = String(rawValue ?? '').trim();
      if (value !== '') properties[match[1]] = value;
    }
    return properties;
  }

  function setBusy(form, builder, busy) {
    form.dataset.ugbBuilderSubmitting = busy ? 'true' : 'false';
    form.toggleAttribute('aria-busy', busy);
    builder.classList.toggle('is-busy', busy);
    form.querySelectorAll('button[name="add"], [data-testid="standalone-add-to-cart"]').forEach((button) => {
      button.disabled = busy;
    });
  }

  function showError(builder, message) {
    const error = builder.querySelector('[data-ugb-builder-error]');
    if (!error) return;
    error.textContent = message || 'We could not add this customized gift to the cart. Please try again.';
    error.hidden = false;
    error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function validateStandardPackaging(builder, state) {
    const result = state || syncStandardPackaging(builder);
    if (result.allowed || !result.wasSelected) return true;

    if (result.group instanceof HTMLDetailsElement) result.group.open = true;
    result.group?.classList.add('is-required-missing');
    showError(builder, standardPackagingLimitMessage(builder, result.itemsTotal));
    return false;
  }

  function validatePackaging(builder) {
    const group = packagingGroup(builder);
    if (!group || group.dataset.required !== 'true') return true;
    const selected = group.querySelector('[data-ugb-addon-option]:checked');
    if (selected) return true;

    group.open = true;
    group.classList.add('is-required-missing');
    showError(builder, 'Please choose gift packaging before adding this gift to the cart.');
    return false;
  }

  async function submitBundle(form, builder, options) {
    const error = builder.querySelector('[data-ugb-builder-error]');
    if (error) error.hidden = true;

    const formData = new FormData(form);
    const mainVariantId = Number(formData.get('id'));
    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    if (!mainVariantId) throw new Error('Please choose the gift option again before adding it to the cart.');

    const bundleId = buildBundleId();
    const composition = buildComposition(options);
    const mainProperties = collectMainProperties(formData);
    mainProperties[BUILDER_KEYS.bundleId] = bundleId;
    mainProperties[BUILDER_KEYS.role] = 'Main';
    mainProperties[BUILDER_KEYS.source] = 'UGB Native';
    mainProperties[BUILDER_KEYS.giftContents] = composition.giftContents;
    mainProperties[BUILDER_KEYS.fulfillmentContents] = composition.fulfillmentContents;
    mainProperties[BUILDER_KEYS.summary] = composition.summary;

    const mainItem = {
      id: mainVariantId,
      quantity,
      properties: mainProperties
    };

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) mainItem.selling_plan = Number(sellingPlan) || sellingPlan;

    const items = [mainItem];
    for (const option of options) {
      const variantId = Number(option.dataset.variantId);
      if (!variantId) continue;

      const fallback = cleanText(option.dataset.variantTitle, 'Gift addition');
      const giftContent = cleanText(option.dataset.giftContent, fallback);
      const fulfillmentContent = cleanText(option.dataset.fulfillmentContent, giftContent);

      items.push({
        id: variantId,
        quantity,
        parent_id: mainVariantId,
        properties: {
          [BUILDER_KEYS.bundleId]: bundleId,
          [BUILDER_KEYS.role]: 'Add-on',
          [BUILDER_KEYS.parent]: builder.dataset.ugbMainProductTitle || 'Custom gift',
          [BUILDER_KEYS.category]: option.dataset.category || 'Gift addition',
          [BUILDER_KEYS.source]: 'UGB Native',
          [BUILDER_KEYS.giftContent]: giftContent,
          [BUILDER_KEYS.fulfillmentContent]: fulfillmentContent
        }
      });
    }

    if (items.length < 2) throw new Error('Please choose gift packaging before adding this gift to the cart.');

    const root = window.Shopify?.routes?.root || '/';
    const addUrl = window.Theme?.routes?.cart_add_url || `${root}cart/add.js`;
    const response = await fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    });

    let payload = null;
    try { payload = await response.json(); } catch (_) {}
    if (!response.ok || payload?.status || payload?.errors) {
      throw new Error(payload?.description || payload?.message || 'We could not add one of the selected gift additions.');
    }

    const cartUrl = window.Theme?.routes?.cart_url || `${root}cart`;
    window.location.assign(cartUrl);
    return true;
  }

  async function handleBuilderAdd(form, builder) {
    if (form.dataset.ugbBuilderSubmitting === 'true') return;

    const error = builder.querySelector('[data-ugb-builder-error]');
    if (error) error.hidden = true;

    const packagingState = syncStandardPackaging(builder);
    updateBuilder(builder);

    if (!form.reportValidity()) return;
    if (!validateStandardPackaging(builder, packagingState)) return;
    if (!validatePackaging(builder)) return;

    const options = selectedOptions(builder);
    setBusy(form, builder, true);

    try {
      await submitBundle(form, builder, options);
    } catch (errorObject) {
      console.error('[UGB native gift builder] Add failed', errorObject);
      showError(builder, errorObject?.message);
      setBusy(form, builder, false);
    }
  }

  function wireBuilderForm(builder) {
    const form = builder.closest('form') || builder.closest('product-form-component')?.querySelector('form');
    if (!(form instanceof HTMLFormElement) || form.dataset.ugbBuilderFormReady === 'true') return;

    form.dataset.ugbBuilderFormReady = 'true';

    const addButtons = Array.from(form.querySelectorAll('button[name="add"], [data-testid="standalone-add-to-cart"]'));
    addButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.type = 'button';
      button.dataset.ugbBuilderButton = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        handleBuilderAdd(form, builder);
      }, true);
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      handleBuilderAdd(form, builder);
    }, true);
  }

  function initBuilder(builder) {
    builder.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const optionLabel = target.closest('.ugb-gift-builder__option');
      const standard = standardPackagingOption(builder);
      if (!optionLabel || !(standard instanceof HTMLInputElement)) return;
      if (standard.disabled && optionLabel.contains(standard)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    builder.addEventListener('change', (event) => {
      const option = event.target.closest?.('[data-ugb-addon-option]');
      if (!option) return;

      const group = option.closest('[data-ugb-builder-group]');
      if (option.checked && group?.dataset.selectionMode === 'single') {
        group.querySelectorAll('[data-ugb-addon-option]').forEach((other) => {
          if (other !== option) other.checked = false;
        });
      }

      updateBuilder(builder);
    });

    updateBuilder(builder);
    wireBuilderForm(builder);
  }

  document.addEventListener('invalid', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const group = target.closest('[data-ugb-builder-message-group]');
    if (group instanceof HTMLDetailsElement) group.open = true;
  }, true);

  function init(root = document) {
    root.querySelectorAll?.('[data-ugb-gift-builder]').forEach((builder) => {
      if (builder.dataset.ugbBuilderReady === 'true') {
        wireBuilderForm(builder);
        updateBuilder(builder);
        return;
      }
      builder.dataset.ugbBuilderReady = 'true';
      initBuilder(builder);
    });
    initMessageGroups(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();